import React, { useState } from "react";
import { CognitoUser } from "amazon-cognito-identity-js";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "react-bootstrap/Button";
import UserPool, { authenticateUser, setAuthToken } from "../cognitoConfig"; // Import necessary functions
import { loginData } from "../redux/features/loginSlice";
import { AppDispatch } from "../redux/store/store";
import { showSuccessToast, showErrorToast } from "../utils/toast/notificationPopup";
import AuthLayout from "../Components/AuthLayout/AuthLayout";
import "../Components/AnalyzePanel/AnalysisPanel.scss";


const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const [userName, setUserName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [passwordResetRequired, setPasswordResetRequired] = useState<boolean>(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { accessToken } = await authenticateUser(userName, password);
      setAuthToken(accessToken);
      console.log("Login successful! Token stored.");
      dispatch(loginData({ userName, isSignedIn: true }));
      navigate("/home");
    } catch (error: any) {
      if (error.newPasswordRequired) {
        setPasswordResetRequired(true);
     
        setCognitoUser(error.user); // Store the user for password reset

      } else {
        showErrorToast(error.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cognitoUser) {
      setLoading(true);
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          setLoading(false);
          const token = session.getIdToken().getJwtToken();
          setAuthToken(token); // Store the new token
          dispatch(loginData({ userName, isSignedIn: true }));
          showSuccessToast("Password updated successfully!");
          setPasswordResetRequired(false); // Reset password reset state
          navigate("/home");
        },
        onFailure: (err) => {
          setLoading(false);
          console.error("Password update failed", err);
          showErrorToast(err.message || "Password update failed. Please try again.");
        },
      });
    }
  };

  return (
    <AuthLayout>
      {!passwordResetRequired ? (
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Email Address</label>
            <input
              type="text"
              placeholder="Enter your email"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="remember-me">
            <input type="checkbox" className="checkbox" />
            <span>Remember for 30 days</span>
            <span
              className="forgot"
              onClick={() => navigate("/forgot-password")}
              style={{ cursor: "pointer" }}
            >
              Forgot Password?
            </span>
          </div>
          <Button
            className="btn btn-primary"
            variant="primary"
            style={{ cursor: "pointer" }}
            type="submit"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
          <div className="create-account">
            Don't have an account? <Link to="/signup">Create an Account</Link> here.
          </div>
        </form>
      ) : (
        <form onSubmit={handleNewPasswordSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <Button
            className="btn btn-primary"
            variant="primary"
            style={{ cursor: "pointer" }}
            type="submit"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default LoginPage;
